import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight, Plus, AlertTriangle, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/dashboard/StatCard';
import { MilestoneCard } from '@/components/dashboard/MilestoneCard';
import { MissedOpportunity } from '@/components/dashboard/MissedOpportunity';
import { AutoImportBanner } from '@/components/dashboard/AutoImportBanner';
import { TransactionRow } from '@/components/transactions/TransactionRow';
import { Transactions, Leakage, Milestones } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR, formatNumber } from '@/lib/format';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip as ReTooltip, Cell } from 'recharts';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, demoLogin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [txns, setTxns] = useState([]);
  const [leakage, setLeakage] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      if (!user) {
        await demoLogin();
      }
      const [s, t, l, m] = await Promise.all([
        Transactions.summary().catch(() => null),
        Transactions.list({ limit: 10 }).catch(() => []),
        Leakage.mine().catch(() => []),
        Milestones.mine().catch(() => []),
      ]);
      setSummary(s);
      setTxns(t || []);
      setLeakage(l || []);
      setMilestones(m || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Phase 17 — refresh KPIs / recent txns / leakage / milestones whenever
  // ANY consumer mutation lands in the app (cards or transactions).
  useEffect(() => {
    const handler = (e) => {
      const ent = e?.detail?.entity;
      if (ent === 'card' || ent === 'transaction') load();
    };
    window.addEventListener('rb:data-changed', handler);
    return () => window.removeEventListener('rb:data-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSpend = summary?.total?.total || 0;
  const totalRewards = summary?.total?.rewards || 0;
  const totalMissed = summary?.total?.missed || 0;
  // Leakage card uses the live future-projection from the leakage detector,
  // which already splits recurring rate-gap (×12) from one-time milestone
  // bonuses (×1). Falls back to historical missed × 12 only when the
  // service returns nothing.
  const oneTimeBonuses = leakage.reduce((s, l) => s + (l.milestone_reward_inr || 0), 0);
  const recurringMissed = leakage.reduce((s, l) => s + Math.max(0, (l.value_inr || 0) - (l.milestone_reward_inr || 0)), 0);
  const missedAnnual = leakage.length
    ? recurringMissed * 12 + oneTimeBonuses
    : totalMissed * 12;
  const txnCount = summary?.total?.count || txns.length;
  // Optimization score — computed server-side from earned / (earned + missed).
  // Returns null until the user has at least one transaction with rewards.
  const optimizationScore = summary?.total?.optimization_score;
  const scoreDisplay = optimizationScore == null ? '—' : String(Math.round(optimizationScore));
  const scoreHelper = optimizationScore == null
    ? (txnCount === 0 ? 'Log a transaction to see your score' : 'Computing…')
    : `Across ${txnCount} transaction${txnCount === 1 ? '' : 's'}`;
  const spendByCategory = (summary?.by_category || []).slice(0, 7).map((c, i) => ({
    ...c,
    color: `hsl(var(--chart-${(i % 5) + 1}))`,
  }));
  const topMilestones = useMemo(() =>
    [...milestones].sort((a, b) => (b.progress_pct || 0) - (a.progress_pct || 0)).slice(0, 4),
    [milestones]
  );
  const missedItems = leakage.slice(0, 3).map((l) => ({
    id: l.id, text: l.title, value: l.value_inr, date: l.date,
  }));

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-muted-foreground gap-2">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-sm">Loading your portfolio…</span>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">Dashboard</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Hello, {(user?.name || 'there').split(' ')[0]}.</h1>
          <p className="mt-1 text-sm text-muted-foreground">Here’s your rewards picture, powered by the deterministic engine + GPT-5 insights.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/leakage')} className="gap-2 border-rose-500/30 text-rose-500 hover:text-rose-500 hover:bg-rose-500/5">
            <AlertTriangle className="h-4 w-4" /> Leakage
          </Button>
          <Button onClick={() => navigate('/recommend')} className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90">
            <Sparkles className="h-4 w-4" /> Recommend
          </Button>
          <Button variant="outline" onClick={() => navigate('/transactions')} className="gap-2">
            <Plus className="h-4 w-4" /> Log
          </Button>
        </div>
      </motion.div>

      {/* Phase 15 — surfaces auto-imported transactions from the overnight Gmail
          background sync. Self-hides when there's nothing to show. */}
      <AutoImportBanner />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard
          label="Optimization Score"
          value={scoreDisplay}
          helper={scoreHelper}
          info="100 = best card every time. Formula: earned / (earned + missed) × 100."
          testId="dashboard-optimization-score"
        />
        <StatCard label="Total Spend" value={formatINR(totalSpend, { compact: true })} helper={`${txnCount} transaction${txnCount === 1 ? '' : 's'}`} />
        <StatCard label="Rewards Earned" value={formatINR(totalRewards)} helper="INR equivalent" />
        <StatCard label="Leakage (annual)" value={formatINR(missedAnnual, { compact: true })} helper={leakage.length ? `${leakage.length} event${leakage.length === 1 ? '' : 's'} flagged` : 'Recoverable'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Milestone progress</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/milestones')} className="gap-1 text-muted-foreground">View all <ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {topMilestones.map((m) => (
              <MilestoneCard key={m.id} cardName={m.card_name} bank={m.bank} milestone={{
                name: m.name, target: m.target_inr, current: m.current_inr, reward: m.reward,
              }} />
            ))}
          </div>
        </div>
        <MissedOpportunity items={missedItems} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Recent transactions</h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/transactions')} className="gap-1 text-muted-foreground">See all <ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="mt-3 -mx-2">
            {txns.slice(0, 6).map((t) => (
              <TransactionRow key={t.id} tx={{ ...t, rewardValue: t.reward_value_inr, rewardCurrency: t.reward_currency, cardName: t.card_name, missed: t.missed_inr }} />
            ))}
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">Spend by category</h2>
            <Badge variant="outline" className="font-mono-num">Mar</Badge>
          </div>
          <div className="mt-4 h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={spendByCategory} layout="vertical" margin={{ left: 4, right: 12 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="category" axisLine={false} tickLine={false} width={80} tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} />
                <ReTooltip cursor={{ fill: 'hsl(var(--surface-2))' }} contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 }} formatter={(v) => formatINR(v)} />
                <Bar dataKey="amount" radius={[6, 6, 6, 6]}>{spendByCategory.map((entry, i) => <Cell key={i} fill={entry.color} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-xs text-muted-foreground">Total <span className="font-mono-num text-foreground">{formatINR(totalSpend)}</span> across {formatNumber(spendByCategory.length)} categories.</div>
        </Card>
      </div>
    </div>
  );
}
